import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AppConfigurationSettings } from "@/v2/apis/appConfiguration/app-configuration-apis";
import {
  getCityAppConfiguration,
  getOrgAppConfiguration,
  upsertCityAppConfiguration,
  upsertOrgAppConfiguration,
} from "@/v2/apis/appConfiguration/app-configuration-apis";
import { queryKeys } from "./keys";

export function useOrgAppConfiguration(orgId: string | null) {
  return useQuery({
    queryKey: queryKeys.appConfiguration.org(orgId ?? ""),
    queryFn: () => getOrgAppConfiguration(orgId!),
    enabled: !!orgId,
  });
}

export function useUpsertOrgAppConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orgId,
      settings,
    }: {
      orgId: string;
      settings: AppConfigurationSettings;
    }) => upsertOrgAppConfiguration(orgId, settings),
    onSuccess: (_data, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.appConfiguration.org(orgId),
      });
    },
  });
}

export function useCityAppConfiguration(cityId: number | null) {
  return useQuery({
    queryKey: queryKeys.appConfiguration.city(cityId ?? 0),
    queryFn: () => getCityAppConfiguration(cityId!),
    enabled: !!cityId,
  });
}

export function useUpsertCityAppConfiguration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cityId,
      settings,
    }: {
      cityId: number;
      settings: AppConfigurationSettings;
    }) => upsertCityAppConfiguration(cityId, settings),
    onSuccess: (_data, { cityId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.appConfiguration.city(cityId),
      });
    },
  });
}
